import { Async } from '@flowus/common';
import { Channel } from '@flowus/common/block/channel';
import { useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { LAST_GET_NOTIFICATION_TIME, UNNAMED_USER } from 'src/common/const';
import { SENDER_ID, request } from 'src/common/request';
import { getImageCdnUrl } from 'src/common/utils/url-utils';
import { segmentsToText } from 'src/editor/utils/editor';
import { invalidateNotifications } from 'src/services/app';
import { setLocalStorage } from 'src/utils/local-storage';
import { useRefreshUnreadState } from 'src/views/main/aside/message-panel/shared';
import { OpenSettingFrom, SettingMenuType } from 'src/views/main/setting-modal/type';
import {
  useOpenSettingModal,
  useOpenUpgradeSpace,
} from 'src/views/main/setting-modal/use-open-setting-modal';
import { getCurrentSpace } from '../space';
import { getUserName } from '../user/use-remark-name';
import { $subscribeUpdate } from 'src/views/main/aside/subscribe/store';

const MASTER_RECORD_KEY = '7dde8eb4-726e-44e2-8611-764d2d7ed0b4';

const setSelfAsMaster = () => {
  setLocalStorage(MASTER_RECORD_KEY, JSON.stringify({ uuid: SENDER_ID, timestamp: Date.now() }));
};

const isMaster = () => {
  const masterRecordStr = localStorage.getItem(MASTER_RECORD_KEY);
  if (masterRecordStr == null) {
    setSelfAsMaster();
    return true;
  }
  const masterRecord = JSON.parse(masterRecordStr) as { uuid: string; timestamp: number };
  return masterRecord.uuid === SENDER_ID;
};

const checkMasterLoop = async () => {
  window.addEventListener('unload', () => {
    const masterRecordStr = localStorage.getItem(MASTER_RECORD_KEY);
    if (masterRecordStr == null) return;
    const masterRecord = JSON.parse(masterRecordStr) as { uuid: string; timestamp: number };
    if (masterRecord.uuid === SENDER_ID) {
      localStorage.removeItem(MASTER_RECORD_KEY);
    }
  });

  const CHECK_INTERVAL = 5000;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const masterRecordStr = localStorage.getItem(MASTER_RECORD_KEY);
    if (masterRecordStr != null) {
      const masterRecord = JSON.parse(masterRecordStr) as { uuid: string; timestamp: number };
      if (
        masterRecord.uuid === SENDER_ID ||
        Date.now() - masterRecord.timestamp >= CHECK_INTERVAL * 2
      ) {
        setSelfAsMaster();
      }
    } else {
      setSelfAsMaster();
    }
    await Async.sleep(CHECK_INTERVAL);
  }
};

void checkMasterLoop();

export const usePullNotifications = () => {
  const timerRef = useRef(0);
  const history = useHistory();
  const openSettingModal = useOpenSettingModal();
  const openUpgradeSpace = useOpenUpgradeSpace();
  const refreshUnreadState = useRefreshUnreadState();

  return useCallback(() => {
    if (!isMaster()) return;

    clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(async () => {
      const { results } = await refreshUnreadState();
      const result = await Promise.all(
        (results ?? []).map(async (it) => {
          const { spaceId } = it;

          if (!spaceId) return null;

          const count = it.unread?.mentions ?? 0;
          if (count) {
            // 后端限制每页最大100条，这里最多取100条，太多了弹的通知太多也没意义。
            return request.editor.getNotificationList(
              spaceId,
              Channel.MENTIONS,
              1,
              Math.min(count, 100)
            );
          }

          // 这个是使用秘笈的推送
          if (it.unread?.strategyGuides) {
            return request.editor.getNotificationList(
              spaceId,
              'strategyGuides',
              1,
              Math.min(it.unread?.strategyGuides, 100)
            );
          }

          // 这个是收件箱
          if (it.unread?.inbox) {
            return request.editor.getNotificationList(
              spaceId,
              Channel.INBOX,
              1,
              Math.min(it.unread?.inbox, 100)
            );
          }

          // 这个是分享通知;
          if (it.unread?.sharing) {
            return request.editor.getNotificationList(
              spaceId,
              Channel.SHARING,
              1,
              Math.min(it.unread?.sharing, 100)
            );
          }

          // 这个是订阅通知;
          if (it.unread?.subscribe) {
            $subscribeUpdate.setState(true);
            return request.editor.getNotificationList(
              spaceId,
              Channel.SUBSCRIBE,
              1,
              Math.min(it.unread?.subscribe, 100)
            );
          }

          // 这个是仅弹消息窗的普通通知
          if (it.unread?.normal) {
            return request.editor.getNotificationList(
              spaceId,
              'normal',
              1,
              Math.min(it.unread?.normal, 100)
            );
          }

          return null;
        })
      );

      const notifications = result.reduce((data, result) => {
        if (result == null) return data;
        const newData = data.slice(0);
        const notifications = (result.notificationIds ?? []).map((it) => {
          return result.recordMap?.notifications?.[it];
        });
        for (const notification of notifications) {
          if (
            notification &&
            notification.spaceId &&
            !notification.read &&
            notification.updatedAt &&
            notification.updatedAt >= Number(localStorage.getItem(LAST_GET_NOTIFICATION_TIME) ?? 0)
          ) {
            if (notification.type === 'commented') {
              const { activityId } = notification;
              const activity =
                activityId == null ? undefined : result.recordMap?.activities?.[activityId];
              const authors = (activity?.edits ?? [])
                .flatMap((it) => it.authors)
                .map(
                  (it) => getUserName(it, result.recordMap?.users?.[it]?.nickname) ?? UNNAMED_USER
                );
              const pageId = activity?.parentId;
              const page = pageId == null ? null : result.recordMap?.blocks?.[pageId];
              const pageTitle = segmentsToText(page?.data.segments) || '未命名页面';
              newData.push({
                title: '评论通知',
                timestamp: notification.updatedAt,
                spaceId: notification.spaceId,
                body: __BUILD_IN__
                  ? [authors.join(','), `commented in ${pageTitle}`].join(' ')
                  : [authors.join('、'), `在 ${pageTitle} 发布了评论`].join(' '),
                channel: notification.channel,
                onClick: () => {
                  window.focus();
                  history.push(`/${pageId}#${notification.parentId}`);
                },
              });
            } else if (
              notification.type === 'userInvited' ||
              notification.type === 'groupInvited'
            ) {
              const authorId = notification.edits?.[0]?.authorId;
              const author = authorId == null ? null : result.recordMap?.users?.[authorId];
              const { spaceId } = notification;
              const space = result.recordMap?.spaces?.[spaceId];
              const group =
                notification.invitedPermissionGroupId == null
                  ? null
                  : (space?.permissionGroups ?? []).find(
                      (it) => it.id === notification.invitedPermissionGroupId
                    );
              const invitee = notification.type === 'groupInvited' ? group?.name : '你';
              const pageId = notification.navigableBlockId;
              const page = pageId == null ? null : result.recordMap?.blocks?.[pageId];
              const pageTitle = segmentsToText(page?.data.segments) || '未命名页面';
              let target = '';
              if (notification.navigableBlockId != null) {
                target = `${pageTitle} 的协作`;
              } else {
                target = `${group?.name} 成员组`;
              }
              newData.push({
                title: '邀请通知',
                timestamp: notification.updatedAt,
                spaceId,
                body: [
                  getUserName(author?.uuid ?? '', '') || author?.nickname,
                  `邀请 ${invitee} 加入 ${target}`,
                ].join(' '),
                channel: notification.channel,
                onClick: () => {
                  window.focus();
                  if (notification.navigableBlockId != null) {
                    history.push(`/${pageId}#${notification.parentId}`);
                  }
                },
              });
            } else if (notification.type === 'userMentioned') {
              const authorId = notification.edits?.[0]?.authorId;
              const author = authorId == null ? null : result.recordMap?.users?.[authorId];
              const { spaceId } = notification;
              const pageId = notification.navigableBlockId;
              const page = pageId == null ? null : result.recordMap?.blocks?.[pageId];
              const pageTitle = segmentsToText(page?.data.segments) || '未命名页面';
              newData.push({
                title: '提及通知',
                timestamp: notification.updatedAt,
                spaceId,
                body: [
                  getUserName(author?.uuid ?? '', author?.nickname),
                  `在 ${pageTitle} 提到你`,
                ].join(' '),
                channel: notification.channel,
                onClick: () => {
                  window.focus();
                  if (notification.navigableBlockId != null) {
                    history.push(`/${pageId}#${notification.parentId}`);
                  }
                },
              });
            } else if (notification.type === 'reminder') {
              const { spaceId } = notification;
              const pageId = notification.navigableBlockId;
              const page = pageId == null ? null : result.recordMap?.blocks?.[pageId];
              const pageTitle = segmentsToText(page?.data.segments) || '未命名页面';
              newData.push({
                title: '提醒通知',
                timestamp: notification.updatedAt,
                spaceId,
                body: `在 ${pageTitle} 提到你`,
                channel: notification.channel,
                onClick: () => {
                  window.focus();
                  history.push(`/${pageId}#${notification.parentId}`);
                },
              });
            } else if (
              notification.type === 'normal' &&
              notification.channel === 'strategyGuides'
            ) {
              const editor = notification.edits?.[0];
              if (editor) {
                newData.push({
                  id: notification.uuid,
                  title: editor.strategyGuidesProperty?.title ?? '',
                  timestamp: notification.updatedAt,
                  spaceId: notification.spaceId,
                  body: editor.strategyGuidesProperty?.description ?? '',
                  channel: notification.channel,
                  onClick: () => {
                    window.focus();
                    window.open(editor.strategyGuidesProperty?.jumpUrl);
                  },
                });
              }
            } else if (notification.channel === Channel.INBOX) {
              if (
                ['personalExpirationBefore', 'teamExpirationBefore'].includes(
                  notification.type ?? ''
                )
              ) {
                // @ts-ignore type
                const expireDays = notification.edits[0]?.expireDays as number;
                if (expireDays > 0) {
                  const space = getCurrentSpace();
                  let icon = space.icon?.value;
                  if (space.icon?.type === 'upload') {
                    icon = getImageCdnUrl(space.icon.value);
                  }
                  newData.push({
                    id: notification.uuid,
                    title: '',
                    timestamp: notification.updatedAt,
                    spaceId: notification.spaceId,
                    body: `[${getCurrentSpace().title}]${expireDays}天后到期`,
                    icon,
                    channel: notification.channel,
                    onClick: () => {
                      window.focus();
                      setTimeout(() => {
                        let element = window.document.querySelector('.message-panel');
                        if (element instanceof HTMLElement) {
                          element.click();
                          element = window.document.querySelector('.inbox-notification');
                          setTimeout(() => {
                            if (element instanceof HTMLElement) {
                              element.click();
                            }
                          }, 100);
                        }
                      }, 100);
                    },
                  });
                }
              }
            } else if (notification.channel === 'normal') {
              const edit = notification.edits?.[0];

              if (edit) {
                newData.push({
                  id: notification.uuid,
                  spaceId: notification.spaceId,
                  channel: notification.channel,
                  timestamp: notification.updatedAt,
                  title: edit.title ?? '',
                  icon: edit.icon ?? '',
                  body: edit.content ?? '',
                  onClick: () => {
                    window.focus();
                    if (edit.action === 'activity') {
                      openSettingModal({
                        defaultMenuType: SettingMenuType.integral,
                        from: OpenSettingFrom.notification_normal,
                      });
                    }
                    if (edit.action === 'upgrade') {
                      openUpgradeSpace(OpenSettingFrom.notification_normal);
                    }
                  },
                });
              }
              // @ts-ignore typecheck
            } else if (notification.type === 'pageSubscribe') {
              const pageId = notification.mentionedBlockId;
              const page = pageId == null ? null : result.recordMap?.blocks?.[pageId];
              const pageTitle = segmentsToText(page?.data.segments) || '未命名页面';
              const edit = notification.edits?.[0];
              edit &&
                newData.push({
                  id: notification.uuid,
                  spaceId: notification.spaceId,
                  channel: notification.channel,
                  timestamp: notification.updatedAt,
                  title: edit.title ?? '',
                  icon: edit.icon ?? '',
                  body: ['有人订阅了', pageTitle].join(''),
                  onClick: () => {
                    window.focus();
                    setTimeout(() => {
                      let element = window.document.querySelector('.message-panel');
                      if (element instanceof HTMLElement) {
                        element.click();
                        element = window.document.querySelector('.sharing-notification');
                        setTimeout(() => {
                          if (element instanceof HTMLElement) {
                            element.click();
                          }
                        }, 100);
                      }
                    }, 100);
                  },
                });
            }
          }
        }

        return newData;
      }, [] as { id?: string; spaceId: string; timestamp: number; title: string; body?: string; icon?: string; onClick?: () => void; channel: any }[]);

      localStorage.setItem(LAST_GET_NOTIFICATION_TIME, `${Date.now()}`);

      if (notifications.length > 0) {
        try {
          await Notification.requestPermission();
          for (const notification of notifications) {
            const nativeNotification = new Notification(notification.title, {
              body: notification.body,
              icon: notification.icon,
            });
            nativeNotification.addEventListener('click', () => {
              notification.onClick?.();
              nativeNotification.close();
              void request.editor
                .setNotificationRead({
                  channel: notification.channel,
                  spaceId: notification.spaceId,
                  timestamp: notification.timestamp,
                })
                .then(() => {
                  invalidateNotifications();
                });
            });
          }
        } catch {
          // DO nothing
        }
      }
    }, 500);
  }, [history, openSettingModal, openUpgradeSpace, refreshUnreadState]);
};
